/**
 * inflationModel.js
 * -----------------------------------------------------------------------------
 * Synthetic UK CPIH inflation projection, statistically calibrated to history.
 *
 * WHY THIS MODELS REAL CPIH BEHAVIOUR
 * Every parameter is estimated directly from the ONS CPIH annual series
 * (series L55O, dataset MM23, 1989-2025) — nothing is assumed. The engine is a
 * first-order autoregressive model with bootstrapped residuals:
 *
 *     x_t - mean  =  phi * (x_{t-1} - mean)  +  shock_t
 *
 * Fitting this to the real series reproduces four properties of actual inflation:
 *
 *   1. Long-run average. `mean` is the historical mean (~2.9%), and because
 *      |phi| < 1 the process is stationary and mean-reverting — over long
 *      horizons it always drifts back to that average instead of wandering off,
 *      mirroring how monetary policy anchors inflation. A naive random walk
 *      would diverge; this does not.
 *
 *   2. Volatility. For a stationary AR(1), Var(x) = Var(shock) / (1 - phi^2).
 *      Resampling the fitted residuals therefore reproduces the historical
 *      unconditional variance (~1.9 percentage points of annual swing).
 *
 *   3. Persistence ("stickiness"). `phi` (~0.68) captures inflation's momentum:
 *      a high year tends to be followed by another high-ish year, and runs of
 *      low years cluster too. This is why the synthetic path shows realistic
 *      multi-year trends rather than implausible year-to-year whiplash.
 *
 *   4. Shock shape, including the fat upper tail. Instead of drawing smooth
 *      Gaussian noise, each year reuses a real historical shock sampled at
 *      random (the "bootstrap"). This preserves the skew and fat tails of the
 *      actual data, so the projection occasionally throws up 1990- or 2022-style
 *      spikes at roughly their historical frequency — events a normal
 *      distribution would systematically under-produce.
 *
 * LIMITATIONS (it is a faithful statistical replica, not an economic forecast):
 *   - Shocks are resampled independently, so it does not model volatility
 *     clustering (sustained turbulent periods) the way a GARCH model would.
 *   - It assumes the future resembles the past: no structural breaks, regime
 *     changes, or knowledge of interest rates, energy prices or output gaps.
 *   - Any single 100-year run has its own realised average that varies around
 *     2.9%; only across many runs does the mean converge. Use a representative
 *     run for a deterministic line, or aggregate many runs for a probability fan.
 * -----------------------------------------------------------------------------
 */

import { useState, useRef, useCallback } from 'react';

/** Historical annual CPIH rate (%), ONS series L55O (MM23), 1989-2025. */
export const ONS_CPIH_SERIES_ID = 'L55O';
export const ONS_CPIH_DATASET_ID = 'MM23';
export const ONS_CPIH_ANNUAL_RATE_URL = 'https://www.ons.gov.uk/economy/inflationandpriceindices/timeseries/l55o/mm23';
export const ONS_CPIH_DATASET_URL = 'https://www.ons.gov.uk/economy/inflationandpriceindices/datasets/consumerpriceindices';

export const CPIH_ANNUAL_1989_2025 = [
  5.7, 8.0, 7.5, 4.6, 2.6, 2.2, 2.7, 2.9, 2.2, 1.8,
  1.7, 1.2, 1.6, 1.5, 1.4, 1.4, 2.1, 2.5, 2.4, 3.5,
  2.0, 2.5, 3.8, 2.6, 2.3, 1.5, 0.4, 1.0, 2.6, 2.3,
  1.7, 1.0, 2.5, 7.9, 6.8, 3.3, 3.9,
];

/** Seedable PRNG (mulberry32). Deterministic for a given seed. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Estimate AR(1) parameters from a history of annual rates.
 * @param {number[]} history
 * @returns {{ mean: number, phi: number, sd: number, residuals: number[] }}
 */
export function calibrate(history) {
  const n = history.length;
  const mean = history.reduce((a, b) => a + b, 0) / n;

  let num = 0;
  let den = 0;
  for (let t = 1; t < n; t++) {
    const prevDev = history[t - 1] - mean;
    num += (history[t] - mean) * prevDev;
    den += prevDev * prevDev;
  }
  const phi = num / den;

  const variance = history.reduce((s, x) => s + (x - mean) ** 2, 0) / n;
  const sd = Math.sqrt(variance);

  const residuals = [];
  for (let t = 1; t < n; t++) {
    residuals.push(history[t] - mean - phi * (history[t - 1] - mean));
  }

  return { mean, phi, sd, residuals };
}

/**
 * Generate one synthetic annual inflation path from the end of `history`
 * through `endYear`.
 *
 * @param {object}   [opts]
 * @param {number[]} [opts.history]   Annual rates (defaults to CPIH 1989-2025).
 * @param {number}   [opts.startYear] Year of history[0] (default 1989).
 * @param {number}   [opts.endYear]   Last projected year (default 2126).
 * @param {number}   [opts.seed]      Omit for a random run; pass to reproduce one.
 * @param {number}   [opts.clampMin]  Floor on the rate (default -3).
 * @param {number}   [opts.clampMax]  Ceiling on the rate (default 18).
 * @param {number}   [opts.decimals]  Rounding for output values (default 1).
 * @returns {{
 *   seed: number,
 *   params: { mean: number, phi: number, volatility: number },
 *   projection: { year: number, value: number }[],
 *   series: { year: number, value: number, type: 'actual' | 'projected' }[],
 *   summary: { mean: number, peak: number, trough: number, endValue: number },
 * }}
 */
export function simulateInflation(opts = {}) {
  const {
    history = CPIH_ANNUAL_1989_2025,
    startYear = 1989,
    endYear = 2126,
    seed,
    clampMin = -3,
    clampMax = 18,
    decimals = 1,
  } = opts;

  const usedSeed = seed ?? Math.floor(Math.random() * 2 ** 32);
  const rand = mulberry32(usedSeed);
  const { mean, phi, sd, residuals } = calibrate(history);

  const factor = 10 ** decimals;
  const round = (v) => Math.round(v * factor) / factor;

  const lastHistYear = startYear + history.length - 1;
  const projection = [];
  let prev = history[history.length - 1];

  for (let year = lastHistYear + 1; year <= endYear; year++) {
    const shock = residuals[Math.floor(rand() * residuals.length)];
    let v = mean + phi * (prev - mean) + shock;
    if (v < clampMin) v = clampMin;
    if (v > clampMax) v = clampMax;
    prev = v;
    projection.push({ year, value: round(v) });
  }

  const series = [
    ...history.map((value, i) => ({
      year: startYear + i,
      value,
      type: /** @type {'actual'} */ ('actual'),
    })),
    ...projection.map((p) => ({ ...p, type: /** @type {'projected'} */ ('projected') })),
  ];

  const vals = projection.map((p) => p.value);
  const summary = {
    mean: round(vals.reduce((a, b) => a + b, 0) / vals.length),
    peak: round(Math.max(...vals)),
    trough: round(Math.min(...vals)),
    endValue: vals[vals.length - 1],
  };

  return {
    seed: usedSeed,
    params: {
      mean: Number(mean.toFixed(3)),
      phi: Number(phi.toFixed(3)),
      volatility: Number(sd.toFixed(3)),
    },
    projection,
    series,
    summary,
  };
}

/**
 * React hook wrapping simulateInflation with a stable rerun() callback.
 * Options are read from a ref, so rerun() always uses the latest props with no
 * stale-closure issues, even if you pass an inline options object.
 *
 *   const { series, summary, seed, rerun } = useInflationProjection({ endYear: 2126 });
 *   // ...render series in your chart...
 *   <button onClick={() => rerun()}>Re-run</button>          // new random path
 *   <button onClick={() => rerun(seed)}>Reproduce this run</button>
 *
 * @param {Parameters<typeof simulateInflation>[0]} [options]
 */
export function useInflationProjection(options = {}) {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [result, setResult] = useState(() => simulateInflation(options));

  const rerun = useCallback((nextSeed) => {
    setResult(simulateInflation({ ...optionsRef.current, seed: nextSeed }));
  }, []);

  return { ...result, rerun };
}
